/**
 * Where everything stands in the village (village screen, Sep 2026; round
 * since the second friends test).
 *
 * The village is a clearing that is ROUND ON SCREEN, with a ring of forest
 * around it. It used to be a tall band that filled a portrait phone, but only
 * half of it fitted at a readable zoom, and players could not tell what they
 * were looking at. A circle on the grid would not do either: the isometric
 * tilt squashes it into a wide, flat ellipse that uses a third of a phone. So
 * the clearing is the set of cells whose SCREEN position lies within a circle —
 * which on the grid is an ellipse along the x + y diagonal, and on the phone a
 * circle that fills the width.
 *
 * The Main Building stands in the middle; the road runs straight down from its
 * door to the gate; resources are round the back, the military by the gate.
 * Every building has a plot from the start. A building not built yet shows a
 * signpost on its plot, so the whole village is visible — and explorable — on
 * day one.
 */

export interface Plot {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  /** Footprint in cells, along x and along y. */
  readonly w: number;
  readonly d: number;
}

/** The middle of the village is cell (CENTRE, CENTRE). */
export const CENTRE = 10;
/** The clearing's radius on screen, in world pixels. */
export const VILLAGE_RADIUS_PX = 264;
/** How far past the clearing the forest reaches, in world pixels. */
export const FOREST_DEPTH_PX = 72;

export const PLOTS: readonly Plot[] = [
  { id: "iron_mine", x: 2, y: 2, w: 2, d: 2 },
  { id: "woodcutter", x: 2, y: 7, w: 2, d: 2 },
  { id: "clay_pit", x: 7, y: 2, w: 2, d: 2 },
  { id: "embassy", x: 6, y: 9, w: 1, d: 1 },
  { id: "shrine", x: 8, y: 5, w: 2, d: 2 },
  { id: "warehouse", x: 5, y: 11, w: 2, d: 1 },
  { id: "marketplace", x: 11, y: 5, w: 2, d: 1 },
  { id: "main_building", x: 10, y: 10, w: 2, d: 2 },
  { id: "granary", x: 8, y: 13, w: 1, d: 1 },
  { id: "residence", x: 13, y: 8, w: 1, d: 1 },
  { id: "grain_farm", x: 9, y: 15, w: 2, d: 2 },
  { id: "stable", x: 15, y: 9, w: 2, d: 1 },
  { id: "engineers_guild", x: 12, y: 15, w: 1, d: 1 },
  { id: "barracks", x: 15, y: 12, w: 2, d: 1 },
  { id: "workshop", x: 13, y: 18, w: 1, d: 1 },
  { id: "rally_point", x: 18, y: 13, w: 1, d: 1 },
  { id: "wall", x: 18, y: 17, w: 1, d: 1 },
];

/** The road, from the gate (bottom of the screen) up to the Main Building's door. */
export const ROAD: readonly (readonly [number, number])[] = [
  [18, 18],
  [17, 18],
  [17, 17],
  [16, 17],
  [16, 16],
  [15, 16],
  [15, 15],
  [14, 15],
  [14, 14],
  [13, 14],
  [13, 13],
  [12, 13],
  [12, 12],
  [11, 12],
];

/** Scenery inside the clearing, kept clear of every building and its tag. */
export const DECOR: readonly { readonly frame: string; readonly x: number; readonly y: number }[] =
  [
    { frame: "decor_lantern", x: 16, y: 18 },
    { frame: "decor_treeCrooked", x: 1, y: 5 },
    { frame: "decor_tree", x: 5, y: 1 },
    { frame: "decor_treeHighRound", x: 1, y: 3 },
    { frame: "decor_rockSmall", x: 6, y: 7 },
    { frame: "decor_tree", x: 13, y: 11 },
    { frame: "decor_treeHigh", x: 4, y: 1 },
    { frame: "decor_tree", x: 1, y: 4 },
    { frame: "decor_treeHighRound", x: 3, y: 1 },
    { frame: "decor_rockSmall", x: 16, y: 15 },
    { frame: "decor_rockSmall", x: 13, y: 12 },
    { frame: "decor_fence", x: 16, y: 19 },
    { frame: "decor_rockSmall", x: 11, y: 13 },
    { frame: "decor_tree", x: 6, y: 14 },
  ];

/** Squared distance of a cell's centre from the middle, measured on screen. */
function screenDistance2(x: number, y: number): number {
  const sx = (x - y) * 32;
  const sy = (x + y - 2 * CENTRE) * 16;
  return sx * sx + sy * sy;
}

/** Ground cells: the clearing the village stands on. */
export function inVillage(x: number, y: number): boolean {
  return screenDistance2(x, y) <= VILLAGE_RADIUS_PX ** 2;
}

/** The ring of forest just outside the clearing. */
export function inForest(x: number, y: number): boolean {
  return !inVillage(x, y) && screenDistance2(x, y) <= (VILLAGE_RADIUS_PX + FOREST_DEPTH_PX) ** 2;
}

function cellsWhere(test: (x: number, y: number) => boolean): [number, number][] {
  const out: [number, number][] = [];
  const lo = -6;
  const hi = 2 * CENTRE + 6;
  // Back to front: rows of equal x + y.
  for (let s = 2 * lo; s <= 2 * hi; s++) {
    for (let x = lo; x <= hi; x++) {
      const y = s - x;
      if (y >= lo && y <= hi && test(x, y)) out.push([x, y]);
    }
  }
  return out;
}

/** Every cell of the clearing, back to front. */
export function villageCells(): [number, number][] {
  return cellsWhere(inVillage);
}

/** Every cell of the forest ring, back to front. */
export function forestCells(): [number, number][] {
  return cellsWhere(inForest);
}

export function plotOf(id: string): Plot | undefined {
  return PLOTS.find((p) => p.id === id);
}

/** Every cell a plot covers. */
export function cellsOf(plot: Plot): [number, number][] {
  const out: [number, number][] = [];
  for (let dx = 0; dx < plot.w; dx++)
    for (let dy = 0; dy < plot.d; dy++) out.push([plot.x + dx, plot.y + dy]);
  return out;
}

/** The windmill's sails turn on the Grain Farm's tower, its first cell. */
export const WINDMILL = { plot: "grain_farm" } as const;

/**
 * Chimney tops, as a cell and a height in world pixels above the ground, for
 * the smoke. Heights follow the models: one storey is ~39 px on screen.
 */
export const CHIMNEYS: readonly {
  readonly plot: string;
  readonly x: number;
  readonly y: number;
  readonly lift: number;
}[] = [
  { plot: "main_building", x: 10, y: 10, lift: 112 },
  { plot: "residence", x: 13, y: 8, lift: 96 },
];
